import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  APN_LINKEDIN_GROUPS, 
  getLinkedInGroupMembers, 
  shareToLinkedInGroups 
} from '../../services/linkedinIntegrationService';

const LinkedInGroups = () => {
  const { currentUser, hasRole } = useAuth();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shareContent, setShareContent] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);

  // Admin-only access check
  if (!hasRole('admin')) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">LinkedIn integration is only available to administrators.</p>
          <p className="text-sm text-gray-500">This feature is used for internal talent sourcing and professional networking.</p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 bg-apn-blue text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const groupCategories = {
    'Professional': Object.values(APN_LINKEDIN_GROUPS).filter(g => g.category === 'Professional'),
    'Graduate': Object.values(APN_LINKEDIN_GROUPS).filter(g => g.category === 'Graduate'),
    'Specialty': Object.values(APN_LINKEDIN_GROUPS).filter(g => g.category === 'Specialty')
  };

  const handleGroupSelect = async (groupId) => {
    setLoading(true);
    try {
      const data = await getLinkedInGroupMembers(groupId);
      setSelectedGroup(data.groupInfo);
      setGroupMembers(data.members);
    } catch (error) {
      console.error('Error loading group members:', error);
      alert('LinkedIn integration is not fully configured. This is a demo interface showing available groups.');
    } finally {
      setLoading(false);
    }
  };

  const handleShareToGroups = async (selectedGroupIds) => {
    try {
      const results = await shareToLinkedInGroups(shareContent, selectedGroupIds);
      console.log('Shared to groups:', results);
      setShowShareModal(false);
      setShareContent('');
      alert('Content sharing simulated successfully! LinkedIn integration requires API configuration.');
    } catch (error) {
      console.error('Error sharing to groups:', error);
      alert('LinkedIn integration is not fully configured. This is a demo interface.');
    }
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-apn-blue text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold">LinkedIn Professional Groups</h1>
            <p className="mt-2 text-lg">Connect with finance professionals and source talent</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Configuration Notice */}
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Demo Mode</h3>
              <p className="mt-1 text-sm text-yellow-700">
                LinkedIn integration is in demo mode. Full functionality requires LinkedIn API configuration and approval.
                This interface shows the planned features for talent sourcing and professional networking.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Groups List */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">APN LinkedIn Groups</h2>
                <button
                  onClick={() => setShowShareModal(true)}
                  className="text-sm bg-apn-blue text-white px-3 py-1 rounded hover:bg-blue-700"
                >
                  Share Content
                </button>
              </div>

              {Object.entries(groupCategories).map(([category, groups]) => (
                <div key={category} className="mb-6">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                    {category}
                  </h3>
                  <div className="space-y-2">
                    {groups.map((group) => (
                      <button
                        key={group.id}
                        onClick={() => handleGroupSelect(group.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedGroup?.id === group.id
                            ? 'border-apn-blue bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm">{group.name}</h4>
                            <p className="text-xs text-gray-500 mt-1">{group.description}</p>
                          </div>
                          <span className="text-xs text-gray-400">
                            {(group.memberCount / 1000).toFixed(1)}k
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div className="mt-6 pt-4 border-t border-gray-200">
                <a
                  href="https://www.linkedin.com/company/accelerated-professionals-network/about/?viewAsMember=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-sm text-apn-blue hover:text-blue-700"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  Follow APN on LinkedIn
                </a>
              </div>
            </div>
          </div>

          {/* Group Details */}
          <div className="lg:col-span-2">
            {selectedGroup ? (
              <div className="bg-white shadow rounded-lg">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{selectedGroup.name}</h2>
                      <p className="text-gray-600 mt-1">{selectedGroup.description}</p>
                      <div className="flex items-center mt-2 text-sm text-gray-500">
                        <span>{selectedGroup.memberCount.toLocaleString()} members</span>
                        <span className="mx-2">•</span>
                        <span className="capitalize">{selectedGroup.category}</span>
                      </div>
                    </div>
                    <a
                      href={selectedGroup.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-apn-blue text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
                    >
                      View on LinkedIn
                    </a>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Members</h3>
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-apn-blue"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {groupMembers.map((member) => (
                        <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div>
                            <h4 className="font-medium text-gray-900">{member.name}</h4>
                            <p className="text-sm text-gray-600">{member.headline}</p>
                            <div className="flex items-center mt-1 text-xs text-gray-500">
                              <span>{member.location}</span>
                              <span className="mx-2">•</span>
                              <span>{member.experience} experience</span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <a
                              href={member.linkedinUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-apn-blue hover:text-blue-700 text-sm"
                            >
                              View Profile
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white shadow rounded-lg p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a LinkedIn Group</h3>
                <p className="text-gray-600">Choose a group from the left to view members and networking opportunities.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share Content Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Share to LinkedIn Groups</h3>
            <textarea
              value={shareContent}
              onChange={(e) => setShareContent(e.target.value)}
              placeholder="What would you like to share with the groups?"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              rows="4"
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setShowShareModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleShareToGroups(['finance-professionals', 'recent-graduates'])}
                className="px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700"
                disabled={!shareContent.trim()}
              >
                Share
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkedInGroups;
